import React, { useEffect, useState } from 'react';
import Option from './Option';
import TopNav from './TopNav';
import { dariWords } from './dariWords';
import { recordSolved } from './stats';
import { reportSolve } from './push';
import { listRecordedWords, audioUrlFor, playLoud } from './audioStorage';

// Vocabulary training: pure word-meaning recall as multiple choice.
// Distinct from the Words quiz (which drills letters/spelling) — here you
// match a whole word to its meaning. Pick a direction, then practise an
// endless run of cards; every correct card counts as a solve (streak/stats).

const DIRECTIONS = [
  {
    key: 'd2e',
    title: 'Dari → English',
    blurb: 'See the Dari word, recall its meaning.',
  },
  {
    key: 'e2d',
    title: 'English → Dari',
    blurb: 'See the meaning, recall the Dari word.',
  },
  {
    key: 'mixed',
    title: 'Mixed',
    blurb: 'Both directions, shuffled each card.',
  },
];

function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

// 3 distractors + the answer, unique by their displayed text, shuffled.
function buildOptions(word, side) {
  const value = (w) => (side === 'dari' ? w.dari : w.english);
  const correct = value(word);
  const seen = new Set([correct]);
  const distractors = [];
  for (const w of shuffle(dariWords)) {
    const v = value(w);
    if (seen.has(v)) continue;
    seen.add(v);
    distractors.push(v);
    if (distractors.length === 3) break;
  }
  const opts = [{ label: correct, isCorrect: true }, ...distractors.map((label) => ({ label }))];
  return shuffle(opts).map((o) => ({
    ...o,
    dir: side === 'dari' ? 'rtl' : undefined,
    lang: side === 'dari' ? 'fa' : undefined,
  }));
}

// One card: a word + which side is shown as the prompt. The answer side is
// the opposite. For 'mixed' the prompt side flips at random per card.
function buildCard(direction, prevDari) {
  let word = dariWords[Math.floor(Math.random() * dariWords.length)];
  if (dariWords.length > 1) {
    while (word.dari === prevDari) {
      word = dariWords[Math.floor(Math.random() * dariWords.length)];
    }
  }
  let promptSide;
  if (direction === 'd2e') promptSide = 'dari';
  else if (direction === 'e2d') promptSide = 'english';
  else promptSide = Math.random() < 0.5 ? 'dari' : 'english';

  const answerSide = promptSide === 'dari' ? 'english' : 'dari';
  return {
    word,
    promptSide,
    answerSide,
    options: buildOptions(word, answerSide),
  };
}

function VocabQuiz({ direction, onChangeDirection }) {
  const [recordedSet, setRecordedSet] = useState(new Set());
  const [card, setCard] = useState(() => buildCard(direction, null));
  const [selected, setSelected] = useState(null);
  const [solvedCount, setSolvedCount] = useState(0);

  useEffect(() => {
    listRecordedWords(dariWords.map((w) => w.dari))
      .then(setRecordedSet)
      .catch(() => {});
  }, []);

  // Restart the run when the direction changes.
  useEffect(() => {
    setCard(buildCard(direction, null));
    setSelected(null);
    setSolvedCount(0);
  }, [direction]);

  const handlePick = (opt) => {
    if (selected && selected.isCorrect) return; // already advancing
    setSelected(opt);
    if (opt.isCorrect) {
      recordSolved();
      reportSolve();
      setSolvedCount((c) => c + 1);
      setTimeout(() => {
        setCard((prev) => buildCard(direction, prev.word.dari));
        setSelected(null);
      }, 550);
    }
  };

  const stateFor = (opt) => {
    if (!selected) return '';
    if (opt === selected) return selected.isCorrect ? 'correct' : 'incorrect';
    return '';
  };

  const promptIsDari = card.promptSide === 'dari';
  const promptText = promptIsDari ? card.word.dari : card.word.english;
  const showListen = promptIsDari && recordedSet.has(card.word.dari);

  return (
    <main className='quiz-card'>
      <div className='vocab-head'>
        <button type='button' className='nav-link' onClick={onChangeDirection}>
          ← Change direction
        </button>
        {solvedCount > 0 && (
          <span className='vocab-count'>{solvedCount} solved</span>
        )}
      </div>

      <div className='vocab-prompt'>
        <div className='vocab-prompt-row'>
          <span
            className={promptIsDari ? 'vocab-prompt-dari' : 'vocab-prompt-english'}
            lang={promptIsDari ? 'fa' : undefined}
            dir={promptIsDari ? 'rtl' : undefined}
          >
            {promptText}
          </span>
          <button
            type='button'
            className={`listen-icon-btn ${showListen ? '' : 'hidden'}`}
            onClick={() => showListen && playLoud(audioUrlFor(card.word.dari))}
            aria-label='Listen'
            title='Listen'
            tabIndex={showListen ? 0 : -1}
            aria-hidden={!showListen}
          >
            🔊
          </button>
        </div>
      </div>

      <div className='question-section'>
        <p>{promptIsDari ? 'What is the meaning?' : 'Which is the Dari word?'}</p>
        {card.options.map((opt, i) => {
          // When the options are Dari words, offer a 🔊 on any that have a
          // recording, so you can hear them while choosing.
          const canPlay = card.answerSide === 'dari' && recordedSet.has(opt.label);
          const trailing = canPlay ? (
            <button
              type='button'
              className='listen-icon-btn option-audio'
              onClick={(e) => { e.stopPropagation(); playLoud(audioUrlFor(opt.label)); }}
              aria-label='Listen'
              title='Listen'
            >
              🔊
            </button>
          ) : undefined;
          return (
            <Option
              key={`${solvedCount}-${i}`}
              label={opt.label}
              state={stateFor(opt)}
              onClick={() => handlePick(opt)}
              dir={opt.dir}
              lang={opt.lang}
              trailing={trailing}
            />
          );
        })}
      </div>
    </main>
  );
}

// How many words per page in the browse/study list.
const BATCH = 12;

// Study mode: every word shown side by side (Dari + meaning), with audio
// where a recording exists. No quiz — just review. Paginated in batches.
function VocabBrowse({ onBack }) {
  const [recordedSet, setRecordedSet] = useState(new Set());
  const [page, setPage] = useState(0);

  useEffect(() => {
    listRecordedWords(dariWords.map((w) => w.dari))
      .then(setRecordedSet)
      .catch(() => {});
  }, []);

  const pageCount = Math.max(1, Math.ceil(dariWords.length / BATCH));
  const start = page * BATCH;
  const batch = dariWords.slice(start, start + BATCH);

  return (
    <main className='quiz-card'>
      <div className='vocab-head'>
        <button type='button' className='nav-link' onClick={onBack}>← Back</button>
        <span className='vocab-count'>
          {start + 1}–{Math.min(start + BATCH, dariWords.length)} of {dariWords.length}
        </span>
      </div>

      <ul className='vocab-browse-list'>
        {batch.map((w) => {
          const has = recordedSet.has(w.dari);
          return (
            <li key={w.dari} className='vocab-browse-row'>
              <span className='vocab-browse-dari' lang='fa' dir='rtl'>{w.dari}</span>
              <span className='vocab-browse-english'>{w.english}</span>
              <button
                type='button'
                className={`listen-icon-btn ${has ? '' : 'hidden'}`}
                onClick={() => has && playLoud(audioUrlFor(w.dari))}
                aria-label={`Listen to ${w.english}`}
                title='Listen'
                tabIndex={has ? 0 : -1}
                aria-hidden={!has}
              >
                🔊
              </button>
            </li>
          );
        })}
      </ul>

      <div className='vocab-pager'>
        <button
          type='button'
          className='nav-link'
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Prev
        </button>
        <span className='vocab-count'>Page {page + 1} of {pageCount}</span>
        <button
          type='button'
          className='nav-link'
          disabled={page >= pageCount - 1}
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
        >
          Next →
        </button>
      </div>
    </main>
  );
}

function DirectionPicker({ onPick }) {
  return (
    <main className='quiz-card home-cover'>
      <div className='home-brand'>
        <h1 className='home-title'>Vocabulary</h1>
        <p className='home-tagline'>Practise word meanings, or browse the list.</p>
      </div>
      <div className='rule-double' aria-hidden='true' />
      <nav className='home-index'>
        {DIRECTIONS.map((d) => (
          <button
            key={d.key}
            type='button'
            className='home-entry'
            onClick={() => onPick(d.key)}
          >
            <span className='home-entry-text'>
              <span className='home-entry-head'>
                <span className='home-entry-title'>{d.title}</span>
              </span>
              <span className='home-entry-blurb'>{d.blurb}</span>
            </span>
            <span className='home-entry-arrow' aria-hidden='true'>→</span>
          </button>
        ))}
        <button
          type='button'
          className='home-entry'
          onClick={() => onPick('browse')}
        >
          <span className='home-entry-text'>
            <span className='home-entry-head'>
              <span className='home-entry-title'>Study list</span>
            </span>
            <span className='home-entry-blurb'>Browse all words side by side, with audio.</span>
          </span>
          <span className='home-entry-arrow' aria-hidden='true'>→</span>
        </button>
      </nav>
    </main>
  );
}

function VocabPage() {
  const [direction, setDirection] = useState(null);

  let content;
  if (direction === 'browse') {
    content = <VocabBrowse onBack={() => setDirection(null)} />;
  } else if (direction) {
    content = <VocabQuiz direction={direction} onChangeDirection={() => setDirection(null)} />;
  } else {
    content = <DirectionPicker onPick={setDirection} />;
  }

  return (
    <div className='screen'>
      <TopNav active='vocab' />
      {content}
    </div>
  );
}

export default VocabPage;
